import * as React from "react";
import { useState, useEffect, useRef } from "react";
import WorldMap from "react-svg-worldmap";
import { 
  AppBar, Box, createTheme, ThemeProvider, Toolbar, Paper, 
  Button, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TextField, InputAdornment, Typography 
} from '@mui/material';
import { brown, lightBlue, lightGreen } from '@mui/material/colors';
import SearchIcon from '@mui/icons-material/Search';
import "./App.css";
import { data } from "./data2.js";

// Import Montserrat in your App.css or index.html!
const theme = createTheme({
  palette: { primary: brown, secondary: lightBlue, error: lightGreen },
  typography: { fontFamily: "'Montserrat', sans-serif" }
});

export default function App() {
  const [mapdata, setData] = useState([]);
  const [muitheme, setTheme] = useState("primary");
  const [color, setColor] = useState("#8d6e63");
  const [units, setUnits] = useState("sq km");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // NEW: State to handle dynamic sizing
  const [mapSize, setMapSize] = useState(window.innerWidth > 1200 ? 1000 : 800);

  useEffect(() => {
    const handleResize = () => {
      // Set map to roughly 80% of screen width, capped at a reasonable size
      const newSize = window.innerWidth * 0.8;
      setMapSize(newSize > 1400 ? 1400 : newSize);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial call
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const tableContainerRef = useRef(null);
  const rowRefs = useRef({});

  // Format data for the map
  const landData = data.map(d => ({ country: d.code.toLowerCase(), value: d.land, name: d.name }));
  const popData = data.map(d => ({ country: d.code.toLowerCase(), value: d.population, name: d.name }));
  const densData = data.map(d => ({ country: d.code.toLowerCase(), value: d.density, name: d.name }));

  useEffect(() => {
    setData([...landData].sort((a, b) => b.value - a.value));
  }, []);

  // Calculate Min/Max for the manual opacity shading
  const maxVal = Math.max(...mapdata.map(d => d.value), 1);
  const minVal = Math.min(...mapdata.map(d => d.value), 0);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const found = mapdata.find(c => c.name.toLowerCase().includes(query));
    if (found) {
      setSelectedCountry(found.country);
      rowRefs.current[found.country]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // This handles both the "Heatmap" shading AND the selection highlight
  const getLayerStyle = (context) => {
    const isSelected = context.countryCode.toLowerCase() === selectedCountry?.toLowerCase();
    const countryData = mapdata.find(d => d.country.toLowerCase() === context.countryCode.toLowerCase());
    
    let opacity = 0.15; // Fallback for no data
    if (countryData) {
      // Calculate opacity between 0.2 and 1.0 based on value
      opacity = 0.2 + ((countryData.value - minVal) / (maxVal - minVal)) * 0.8;
    }

    return {
      fill: color,
      fillOpacity: opacity,
      stroke: isSelected ? "#000" : "#ffffff", // Black border for selection, white for others
      strokeWidth: isSelected ? 2.5 : 0.5,
      strokeOpacity: isSelected ? 1 : 0.3,
      cursor: "pointer",
      transition: "all 0.2s ease"
    };
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="App" style={{ height: '100vh', width: '100vw', overflow: 'hidden', position: 'relative', backgroundColor: '#f5f5f5' }}>
        
        {/* Title & Search - Top Left */}
        <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 1000, pointerEvents: 'none' }}>
          <Typography variant="h4" sx={{ fontWeight: 900, color: theme.palette[muitheme].main, letterSpacing: -1, mb: 1, pointerEvents: 'auto' }}>
            REPUBLICS
          </Typography>
          <TextField
            size="small"
            placeholder="Search country..."
            value={searchQuery}
            onChange={handleSearch}
            sx={{ width: 280, bgcolor: 'white', borderRadius: 1, boxShadow: 2, pointerEvents: 'auto' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
        </Box>

        {/* Map - Explicitly sized to fill space */}
        <Box className="map-wrapper" sx={{ width: '100%', height: '100%',display:"grid",placeItems:"center"}}>
          <WorldMap
            color={color}
            valueSuffix={units}
            size={mapSize} // Using the dynamic size we set up earlier
            data={mapdata}
            styleFunction={getLayerStyle}
            
            // 1. Handle hover ON
            onMouseEnter={(ctx) => {
              const code = ctx.countryCode.toLowerCase();
              setSelectedCountry(code);
              
              // 2. Optional: Scroll the table to this country automatically
              if (rowRefs.current[code]) {
                rowRefs.current[code].scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }}
            
            // 3. Handle hover OFF
            onMouseLeave={() => setSelectedCountry(null)}
            
            onClickFunction={(ctx) => setSelectedCountry(ctx.countryCode.toLowerCase())}
          />
        </Box>

        {/* Table - Top Right */}
        <Box className="table-overlay" sx={{ position: 'absolute', top: 20, right: 20, width: 320, zIndex: 1000 }}>
          <TableContainer component={Paper} sx={{ maxHeight: 400, boxShadow: 4, borderRadius: 2,border: `1px solid ${theme.palette[muitheme].main}` }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: theme.palette[muitheme].main, color: 'white', fontWeight: 'bold' }}>
                    Rank
                  </TableCell>
                  <TableCell sx={{ bgcolor: theme.palette[muitheme].main, color: 'white', fontWeight: 'bold' }}>
                    Country
                  </TableCell>
                  <TableCell sx={{ bgcolor: theme.palette[muitheme].main, color: 'white', fontWeight: 'bold' }} align="right">
                    {/* This now changes dynamically */}
                    {units} 
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mapdata.map((item, index) => (
                  <TableRow 
                    key={item.country}
                    ref={(el) => (rowRefs.current[item.country] = el)}
                    selected={selectedCountry === item.country}
                    onMouseEnter={() => setSelectedCountry(item.country)}
                    onMouseLeave={()=>setSelectedCountry(null)}
                    sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: 'rgba(0,0,0,0.05) !important' } }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{item.name}</TableCell>
                    <TableCell align="right">{item.value.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Bottom Nav */}
        <AppBar 
          position="fixed" 
          elevation={0} // Removes the shadow line
          sx={{ 
            top: 'auto', 
            bottom: 0, 
            left:"35vw",
            width:'30vw',
            bgcolor: 'transparent', // Fully clear background
            backgroundImage: 'none', // Material UI sometimes adds gradients
            pointerEvents: 'none'    // Makes the bar "clickable-through"
          }}
        >
          <Toolbar 
            sx={{               
              gap: 2, 
              pointerEvents: 'auto' // RE-ENABLES clicking for the buttons inside
            }}
          >
            <Button 
              variant="contained" 
              disableElevation 
              onClick={() => { 
                setData([...landData].sort((a,b)=>b.value-a.value)); 
                setTheme("primary"); 
                setColor("#8d6e63"); 
                setUnits("sq km"); 
              }}
            >
              Land
            </Button>
            
            <Button 
              variant="contained" 
              disableElevation 
              color="secondary" 
              onClick={() => { 
                setData([...popData].sort((a,b)=>b.value-a.value)); 
                setTheme("secondary"); 
                setColor("#03a9f4"); 
                setUnits("people"); 
              }}
            >
              Population
            </Button>
            
            <Button 
              variant="contained" 
              disableElevation 
              color="error" 
              onClick={() => { 
                setData([...densData].sort((a,b)=>b.value-a.value)); 
                setTheme("error"); 
                setColor("#33691e"); 
                setUnits("ppl/km²"); 
              }}
            >
              Density
            </Button>
          </Toolbar>
        </AppBar>
      </div>
    </ThemeProvider>
  );
}